import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BookOpen, Trophy, User, ShieldCheck, LayoutDashboard, GraduationCap, Heart, Zap, Briefcase, Building2 } from 'lucide-react';
import { COLORS } from '@nzassa/shared';
import { useAuthStore } from '@/stores/authStore';

export function BottomNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  const hiddenPaths = ['/', '/auth', '/connexion', '/admin-login'];
  const isPlayerPage = location.pathname.startsWith('/courses/');
  if (hiddenPaths.includes(location.pathname) || isPlayerPage) return null;

  const BASE_NAV_ITEMS = [
    { path: '/dashboard', label: t('nav.cours'), icon: BookOpen },
    { path: '/leaderboard', label: t('nav.classement'), icon: Trophy },
    { path: '/profile', label: t('nav.profil'), icon: User },
  ];

  function getRoleNavItems(role?: string) {
    if (!role) return BASE_NAV_ITEMS;

    switch (role) {
      case 'SUPER_USER_ADMIN':
      case 'SUPER_ADMIN':
        return [
          { path: '/pm', label: t('nav.pilotage'), icon: LayoutDashboard },
          { path: '/admin', label: t('nav.admin'), icon: ShieldCheck },
          ...BASE_NAV_ITEMS,
        ];
      case 'ADMIN_ANSUT':
      case 'TEAM_ANSUT':
      case 'ADMIN_COLLEGE':
      case 'PROGRAM_MANAGER':
        return [
          { path: '/pm', label: t('nav.pilotage'), icon: LayoutDashboard },
          ...BASE_NAV_ITEMS,
        ];
      case 'FORMATEUR_PRINCIPAL':
      case 'FORMATEUR_ASSISTANT':
      case 'FORMATEUR':
        return [
          { path: '/formateur', label: t('nav.formation'), icon: GraduationCap },
          ...BASE_NAV_ITEMS,
        ];
      case 'MENTOR':
        return [
          { path: '/mentor', label: t('nav.mentorat'), icon: Heart },
          ...BASE_NAV_ITEMS,
        ];
      case 'EQUIPE_COLLEGE':
      case 'COMMUNITY_BUILDER':
        return [
          { path: '/community', label: t('nav.communaute'), icon: Zap },
          ...BASE_NAV_ITEMS,
        ];
      case 'PARTENAIRE':
        return [
          { path: '/partenaire', label: t('nav.programme'), icon: Briefcase },
          ...BASE_NAV_ITEMS,
        ];
      case 'BAILLEUR':
        return [
          { path: '/bailleur', label: t('nav.impact'), icon: Building2 },
          ...BASE_NAV_ITEMS,
        ];
      default:
        return BASE_NAV_ITEMS;
    }
  }

  const items = getRoleNavItems(user?.role);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 sm:hidden border-t border-slate-200 bg-white/95 backdrop-blur-lg safe-area-bottom" role="navigation" aria-label={t('nav.navigation_mobile')}>
      <div className="flex items-center justify-around px-2 py-1">
        {items.map(({ path, label, icon: Icon }) => {
          const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
              className={`flex flex-col items-center gap-0.5 py-2 px-3 rounded-xl transition-colors min-w-[60px] ${
                isActive ? 'text-white' : 'text-slate-400 active:text-slate-600'
              }`}
              style={isActive ? { color: COLORS.blue } : undefined}
            >
              <div
                className={`p-1.5 rounded-xl transition-all ${
                  isActive ? 'bg-blue-50 scale-110' : ''
                }`}
              >
                <Icon
                  className="h-5 w-5"
                  style={isActive ? { color: COLORS.blue } : undefined}
                />
              </div>
              <span
                className={`text-[10px] font-semibold ${
                  isActive ? '' : 'text-slate-400'
                }`}
                style={isActive ? { color: COLORS.blue } : undefined}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
